import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { knowledgeAPI, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  FolderTree, Plus, RefreshCw, Trash2, AlertTriangle, Upload, FolderUp, FileArchive, Loader2, Link2,
} from 'lucide-react';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';

function notifyKbIndexUpdated() {
  window.dispatchEvent(new CustomEvent('kb-index-updated'));
}

export default function KBSourceMountsPanel() {
  const [mounts, setMounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    label: '',
    subpath: '',
    include_globs: '**/*',
    exclude_globs: '',
    enabled: true,
    storage_kind: 'host',
  });
  const [busy, setBusy] = useState('');
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploadMode, setUploadMode] = useState('merge');
  const [showScopeCta, setShowScopeCta] = useState(false);
  const folderInputRefs = useRef({});
  const zipInputRefs = useRef({});
  const prevOpRef = useRef({});

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await knowledgeAPI.listSourceMounts();
      const list = res.data?.mounts || [];
      setMounts(list);

      list.forEach((m) => {
        const prev = prevOpRef.current[m.mount_id] || {};
        if (prev.sync_state === 'syncing' && m.sync_state !== 'syncing') {
          const n = m.last_reindex_files_indexed ?? 0;
          const u = m.last_reindex_files_unchanged ?? 0;
          const s = m.last_reindex_files_skipped ?? 0;
          if (m.last_reindex_status === 'ok') {
            toast.success(`Reindex OK (${m.mount_id}): ${n} nuevos, ${u} sin cambios, ${s} saltados`);
            notifyKbIndexUpdated();
          } else if (m.last_reindex_status === 'partial') {
            toast.warning(`Reindex parcial (${m.mount_id}): ${n} indexados — ${(m.last_reindex_error || '').slice(0, 120)}`);
            notifyKbIndexUpdated();
          } else if (m.last_reindex_status === 'cancelled') {
            toast.info(`Reindex cancelado (${m.mount_id})`);
          } else if (m.last_reindex_status === 'error') {
            toast.error(m.last_reindex_error || `Error al reindexar (${m.mount_id})`);
          }
        }
        prevOpRef.current[m.mount_id] = {
          sync_state: m.sync_state,
          sync_phase: m.sync_phase,
        };
      });
    } catch (e) {
      if (!silent) toast.error('Error cargando mounts');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const anyRunning = mounts.some((m) => m.sync_state === 'syncing');

  useEffect(() => {
    if (!anyRunning) return undefined;
    const id = setInterval(() => load(true), 2000);
    return () => clearInterval(id);
  }, [anyRunning, load]);

  const createMount = async () => {
    if (!form.label.trim()) {
      toast.error('Nombre es obligatorio');
      return;
    }
    if (form.storage_kind === 'host' && !form.subpath.trim()) {
      toast.error('Subruta es obligatoria para mounts host');
      return;
    }
    setBusy('create');
    try {
      await knowledgeAPI.createSourceMount({
        label: form.label.trim(),
        subpath: form.subpath.trim(),
        include_globs: form.include_globs.split(',').map((s) => s.trim()).filter(Boolean),
        exclude_globs: form.exclude_globs.split(',').map((s) => s.trim()).filter(Boolean),
        enabled: form.enabled,
        storage_kind: form.storage_kind,
      });
      toast.success('Mount creado');
      setForm({
        label: '',
        subpath: '',
        include_globs: '**/*',
        exclude_globs: '',
        enabled: true,
        storage_kind: 'host',
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || formatApiError(e) || 'Error creando mount');
    } finally {
      setBusy('');
    }
  };

  const reindexMount = async (mountId) => {
    setBusy(mountId);
    try {
      const res = await knowledgeAPI.reindexSourceMount(mountId);
      const data = res.data || {};
      if (data.started === false && data.already_running) {
        toast.info('Ya hay un reindex en curso para este mount');
      } else if (data.started) {
        toast.message('Reindex iniciado — seguí el progreso abajo');
        await load(true);
      } else {
        toast.success(
          `Reindexado: ${data.files_indexed || 0} nuevos, ${data.files_unchanged || 0} sin cambios`,
        );
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || formatApiError(e) || 'Error reindexando');
    } finally {
      setBusy('');
    }
  };

  const cancelMountOp = async (mountId) => {
    try {
      await knowledgeAPI.cancelSourceMountOperation(mountId);
      toast.info('Cancelacion solicitada');
      load(true);
    } catch (e) {
      toast.error(formatApiError(e) || 'Error cancelando');
    }
  };

  const deleteMount = async () => {
    const mountId = deleteTarget?.mount_id;
    if (!mountId) return;
    setBusy(`del-${mountId}`);
    try {
      const res = await knowledgeAPI.deleteSourceMount(mountId);
      const hard = res.data?.hard;
      toast.success(
        hard
          ? 'Mount eliminado (archivos, índice y registro)'
          : 'Mount eliminado (deshabilitado; el host no se toca)',
      );
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e) || 'Error eliminando mount');
    } finally {
      setBusy('');
    }
  };

  const purgeAndReindex = async () => {
    setBusy('purge');
    try {
      const res = await knowledgeAPI.purgeReindexMounts();
      toast.success(`Purga OK: ${res.data?.qdrant_chunks_removed || 0} chunks legacy, ${res.data?.mounts_reindexed || 0} mounts`);
      load();
    } catch (e) {
      toast.error('Error en purga/reindex');
    } finally {
      setBusy('');
      setPurgeOpen(false);
    }
  };

  const handleUploadResult = (data) => {
    const idx = data.reindex || {};
    toast.success(`Subidos ${data.files_written || 0} archivos.`);
    if (idx.started) {
      toast.message('Indexacion en curso — seguí el progreso abajo');
      setShowScopeCta(true);
      load(true);
    } else if (idx.already_running) {
      toast.info('Upload OK; reindex ya en curso en otro proceso');
    } else if (idx.files_indexed != null) {
      toast.success(`Indexados: ${idx.files_indexed || 0}`);
      setShowScopeCta(true);
      load();
    }
    if (idx.last_reindex_error) {
      toast.warning(`Algunos archivos no indexaron: ${idx.last_reindex_error.slice(0, 160)}`);
    }
  };

  const uploadFolder = async (mountId, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(`up-${mountId}`);
    try {
      const fd = new FormData();
      fd.append('mode', uploadMode);
      fd.append('reindex', 'true');
      const rels = files.map((f) => f.webkitRelativePath || f.name);
      fd.append('relative_paths', JSON.stringify(rels));
      files.forEach((f) => fd.append('files', f));
      const res = await knowledgeAPI.uploadSourceMountFiles(mountId, fd);
      handleUploadResult(res.data || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || formatApiError(e) || 'Error subiendo carpeta');
    } finally {
      setBusy('');
    }
  };

  const uploadZip = async (mountId, file) => {
    if (!file) return;
    setBusy(`zip-${mountId}`);
    try {
      const fd = new FormData();
      fd.append('mode', uploadMode);
      fd.append('reindex', 'true');
      fd.append('file', file);
      const res = await knowledgeAPI.uploadSourceMountZip(mountId, fd);
      handleUploadResult(res.data || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || formatApiError(e) || 'Error subiendo ZIP');
    } finally {
      setBusy('');
    }
  };

  const enabledMounts = mounts.filter((m) => m.enabled !== false);

  return (
    <div className="space-y-4" data-testid="kb-source-mounts-panel">
      {showScopeCta && enabledMounts.length > 0 && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900">
          <Link2 className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold">Suscribí la cápsula a los mounts</p>
            <p className="text-amber-800 mt-0.5">
              Los archivos se indexan a nivel tenant, pero el agente solo los ve si la cápsula tiene
              {' '}<code className="text-[10px]">kb_source_access</code> configurado.
            </p>
            <Link
              to="/capsule-config?tab=kb"
              className="inline-block mt-1.5 text-amber-700 underline font-medium hover:text-amber-900"
            >
              Ir a Config cápsula → Conocimiento
            </Link>
          </div>
          <button
            type="button"
            className="text-amber-500 hover:text-amber-700 text-lg leading-none"
            onClick={() => setShowScopeCta(false)}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
      )}

      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <FolderTree className="w-4 h-4" /> Origenes locales (mounts)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-zinc-500">
            <b>Host</b>: subcarpetas bajo <span className="font-mono">/data/sources</span> (solo lectura; las carga ops).
            {' '}<b>Administrable</b>: volumen RW; subís carpeta o ZIP desde la UI e indexás.
            Cada cápsula se suscribe a los mounts que necesite.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Tipo</Label>
              <select
                className="w-full border border-brand-border rounded-sm h-9 px-2 text-xs bg-white"
                value={form.storage_kind}
                onChange={(e) => setForm({ ...form, storage_kind: e.target.value })}
              >
                <option value="host">Host (solo lectura)</option>
                <option value="managed">Administrable (upload UI)</option>
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Nombre</Label>
              <Input placeholder="Aplicaciones Apex" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">
                {form.storage_kind === 'managed' ? 'Subruta lógica (opcional)' : 'Subruta host'}
              </Label>
              <Input
                placeholder={form.storage_kind === 'managed' ? 'se usa el id del mount si vacío' : 'capsula_sistemas/docs'}
                value={form.subpath}
                onChange={(e) => setForm({ ...form, subpath: e.target.value })}
                className="font-mono text-xs"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Include globs (coma)</Label>
              <Input value={form.include_globs} onChange={(e) => setForm({ ...form, include_globs: e.target.value })} className="font-mono text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Exclude globs (coma)</Label>
              <Input value={form.exclude_globs} onChange={(e) => setForm({ ...form, exclude_globs: e.target.value })} className="font-mono text-xs" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
            <span className="text-xs text-zinc-600">Habilitado</span>
          </div>
          <Button size="sm" onClick={createMount} disabled={busy === 'create'}>
            <Plus className="w-3 h-3 mr-1" /> Agregar mount
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row justify-between py-3">
          <CardTitle className="text-sm">Mounts declarados</CardTitle>
          <div className="flex gap-2 items-center">
            <label className="text-[10px] text-zinc-500 flex items-center gap-1">
              Upload:
              <select
                className="border rounded-sm h-7 px-1 text-[10px] bg-white"
                value={uploadMode}
                onChange={(e) => setUploadMode(e.target.value)}
              >
                <option value="merge">Fusionar</option>
                <option value="replace">Reemplazar</option>
              </select>
            </label>
            <Button size="sm" variant="outline" onClick={() => load()} disabled={loading}>
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" variant="destructive" onClick={() => setPurgeOpen(true)} disabled={!!busy}>
              <AlertTriangle className="w-3 h-3 mr-1" /> Purga legacy + reindex
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {mounts.length === 0 ? (
            <p className="text-sm text-zinc-400">Sin mounts. El auto-scan solo indexa mounts declarados.</p>
          ) : (
            <div className="space-y-2">
              {mounts.map((m) => {
                const managed = (m.storage_kind || 'host') === 'managed';
                const running = m.sync_state === 'syncing';
                const prog = m.sync_progress || {};
                const pct = prog.total > 0 ? Math.round((prog.current / prog.total) * 100) : 0;
                return (
                  <div key={m.mount_id} className="border rounded-sm p-2 text-xs space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold flex items-center gap-1">
                          {running && <Loader2 className="w-3 h-3 animate-spin text-blue-500" />}
                          {m.label}{' '}
                          <span className="text-zinc-400 font-normal">({m.mount_id})</span>
                          <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-sm ${managed ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-600'}`}>
                            {managed ? 'administrable' : 'host'}
                          </span>
                        </div>
                        <div className="text-zinc-500 font-mono truncate">{m.subpath || '(raiz)'}</div>
                        <div className="text-zinc-400">
                          Ultimo reindex: {m.last_reindex || 'nunca'} — {m.last_reindex_status || 'pending'}
                          {m.last_reindex_files_indexed != null ? ` · idx ${m.last_reindex_files_indexed}` : ''}
                          {m.last_reindex_files_skipped != null ? ` · skip ${m.last_reindex_files_skipped}` : ''}
                        </div>
                        {running && (
                          <div className="mt-1 space-y-1">
                            <div className="text-blue-700 text-[10px]">
                              {m.sync_phase || 'En curso...'}
                              {prog.total > 0 && ` · ${prog.current}/${prog.total} archivos`}
                            </div>
                            {prog.current_file && (
                              <div className="text-zinc-500 text-[10px] truncate" title={prog.current_file}>
                                {prog.current_file}
                              </div>
                            )}
                            {prog.total > 0 && (
                              <div className="h-1 bg-zinc-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-blue-500 transition-all duration-300"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                        {!running && m.last_reindex_error ? (
                          <div className="text-amber-700 text-[10px] mt-0.5 break-all">{m.last_reindex_error}</div>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-1 justify-end shrink-0 max-w-[240px]">
                        {managed && (
                          <>
                            <input
                              type="file"
                              className="hidden"
                              multiple
                              {...{ webkitdirectory: '', directory: '' }}
                              ref={(el) => { folderInputRefs.current[m.mount_id] = el; }}
                              onChange={(e) => {
                                uploadFolder(m.mount_id, e.target.files);
                                e.target.value = '';
                              }}
                            />
                            <input
                              type="file"
                              className="hidden"
                              accept=".zip,application/zip"
                              ref={(el) => { zipInputRefs.current[m.mount_id] = el; }}
                              onChange={(e) => {
                                uploadZip(m.mount_id, e.target.files?.[0]);
                                e.target.value = '';
                              }}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={!!busy || running}
                              onClick={() => folderInputRefs.current[m.mount_id]?.click()}
                              title="Subir carpeta"
                            >
                              <FolderUp className="w-3 h-3 mr-0.5" /> Carpeta
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={!!busy || running}
                              onClick={() => zipInputRefs.current[m.mount_id]?.click()}
                              title="Subir ZIP"
                            >
                              <FileArchive className="w-3 h-3 mr-0.5" /> ZIP
                            </Button>
                          </>
                        )}
                        {running ? (
                          <Button size="sm" variant="outline" onClick={() => cancelMountOp(m.mount_id)}>
                            Cancelar
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" disabled={busy === m.mount_id} onClick={() => reindexMount(m.mount_id)}>
                            <Upload className="w-3 h-3 mr-0.5" /> Reindexar
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy === `del-${m.mount_id}` || running}
                          onClick={() => setDeleteTarget(m)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmActionDialog
        open={purgeOpen}
        onOpenChange={setPurgeOpen}
        title="Purgar indice legacy y reindexar mounts"
        description="Elimina chunks sin mount_id/repo_id/capsule_id de Qdrant y reindexa todos los mounts activos. Esta accion no se puede deshacer."
        confirmLabel="Purgar y reindexar"
        onConfirm={purgeAndReindex}
        destructive
      />

      <ConfirmActionDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={`Eliminar mount ${deleteTarget?.label || deleteTarget?.mount_id || ''}`}
        description={
          deleteTarget?.storage_kind === 'managed'
            ? 'Borrado completo: elimina archivos y subcarpetas en disco, índice (Qdrant/Mongo) y el registro del mount. No se puede deshacer.'
            : 'Deshabilita el mount (soft-delete). No borra archivos bajo /data/sources (contenido de ops).'
        }
        confirmLabel="Eliminar"
        onConfirm={deleteMount}
        destructive
      />
    </div>
  );
}
